import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Interactive } from "../src/interactions/Interactive";
import { useInteractionStore } from "../src/stores/useInteraction";
import { emitEvent, addEventListener } from "../src/events/eventBus";

const STANDBY_INDEX = 2;
const POST_INTERACTION_INDEX = 1;
const PROXIMITY_RADIUS = 10;
const ROTATION_DURATION = 0.2;

export default function SkeletonCompanion({
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = 1,
    targetRef = null,
    ...props
}) {
    const group = useRef();
    const interactiveRef = useRef(null);
    const cineModeTriggeredRef = useRef(false);
    const cineTimeoutRef = useRef(null);
    const postInteractionCompleteRef = useRef(false);
    const isMountedRef = useRef(true);
    const { scene, animations } = useGLTF("/interactive/skeleton1.glb");
    const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
    const { actions } = useAnimations(animations, group);
    const modelBounds = useMemo(() => new THREE.Box3(), []);
    const modelCenter = useMemo(() => new THREE.Vector3(), []);

    const actionNames = useMemo(() => Object.keys(actions || {}), [actions]);
    const [currentLoopIndex, setCurrentLoopIndex] = useState(STANDBY_INDEX);
    const getActionName = (index) =>
        actionNames.length > 0 ? actionNames[Math.min(index, actionNames.length - 1)] : undefined;
    const loopActionName = getActionName(currentLoopIndex);

    const baseEuler = useMemo(
        () => new THREE.Euler(rotation[0], rotation[1], rotation[2]),
        [rotation]
    );
    const skeletonWorldPos = useMemo(() => new THREE.Vector3(), []);
    const targetWorldPos = useMemo(() => new THREE.Vector3(), []);
    const lookDirection = useMemo(() => new THREE.Vector3(), []);

    const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);

    const camera = useThree((state) => state.camera);
    const playerHidden = useInteractionStore((state) => state.playerHidden);

    const lerpAngle = useCallback((current, target, alpha) => {
        let diff = ((target - current + Math.PI) % (Math.PI * 2)) - Math.PI;
        if (diff < -Math.PI) diff += Math.PI * 2;
        return current + diff * alpha;
    }, []);

    const triggerCineMode = useCallback(() => {
        if (cineModeTriggeredRef.current) return;
        cineModeTriggeredRef.current = true;

        const state = useInteractionStore.getState();
        state.clearHudMessage();
        state.setPlayerHidden(true);
        state.deactivatePrompt("skeleton-companion");
        state.setCameraOverride(true, {
            position: [4.25, 10, 22.89],
            target: [4.71, -0.07, 33.51],
            transitionDuration: 1.8,
        });

        emitEvent("cineModeStart");

        if (cineTimeoutRef.current !== null) {
            clearTimeout(cineTimeoutRef.current);
        }

        cineTimeoutRef.current = window.setTimeout(() => {
            const exitState = useInteractionStore.getState();
            exitState.setCameraOverride(false);
            exitState.setPlayerHidden(false);
            exitState.clearHudMessage();
            cineTimeoutRef.current = null;
        }, 3000);

        if (interactiveRef.current) {
            interactiveRef.current.setWithinRadius(false);
            interactiveRef.current.dispose();
            interactiveRef.current = null;
        }
    }, []);

    const handleInteract = useCallback(() => {
        if (cineModeTriggeredRef.current) return;

        const { playerHidden: hidden } = useInteractionStore.getState();
        if (!hidden) {
            triggerCineMode();
        }
    }, [triggerCineMode]);

    const interactive = useMemo(() => {
        const instance = new Interactive({
            id: "skeleton-companion",
            promptText: "Interactuar [E]",
            hudText: "Mayoneso: Estaba bien a gusto en mi cantón viendo paw patrol y chingandome unas papitas, cuando me dieron ganas de chingarme unas quesadillas con quesobadón. Por lo que me dirigí a la cocina, pero de repente un remolino me absorbió y ahora terminé aquí contigo. pero ni modos, ya me la pelé, ¡es hora de sacar los prohibidos con unos cumbiones! jsjsjsjs. \n Por suerte traje a unas chiquillas para festejar.",
            radius: PROXIMITY_RADIUS,
            cameraConfig: {
                position: [-8.69, 2.5, 38.8],
                transitionDuration: 1.2,
            },
            onInteract: handleInteract,
            getCameraTarget: () => {
                if (!group.current) return null;
                modelBounds.setFromObject(group.current);
                modelBounds.getCenter(modelCenter);
                return [modelCenter.x, modelCenter.y, modelCenter.z];
            },
        });

        interactiveRef.current = instance;
        return instance;
    }, [handleInteract, modelBounds, modelCenter]);

    useEffect(() => {
        return () => {
            if (cineTimeoutRef.current !== null) {
                clearTimeout(cineTimeoutRef.current);
                cineTimeoutRef.current = null;
            }
            if (interactiveRef.current === interactive) {
                interactiveRef.current = null;
            }
            isMountedRef.current = false;
            interactive.dispose();
        };
    }, [interactive]);

    useEffect(() => {
        const cleanup = addEventListener("cineModeStart", () => {
            if (!isMountedRef.current || postInteractionCompleteRef.current) {
                return;
            }
            postInteractionCompleteRef.current = true;
            setCurrentLoopIndex(POST_INTERACTION_INDEX);
        });

        return cleanup;
    }, []);

    useEffect(() => {
        clonedScene.traverse((child) => {
            if (child.isMesh || child.isSkinnedMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }, [clonedScene]);

    useEffect(() => {
        if (group.current) {
            group.current.rotation.set(baseEuler.x, baseEuler.y, baseEuler.z);
        }
    }, [baseEuler]);

    useEffect(() => {
        if (!actions || !loopActionName) return undefined;

        const loopAction = actions[loopActionName];
        if (!loopAction) return undefined;

        loopAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.1).play();
        loopAction.clampWhenFinished = false;

        return () => {
            loopAction.stop();
        };
    }, [actions, loopActionName]);

    useFrame((_, delta) => {
        const skeletonGroup = group.current;
        if (!skeletonGroup) return;

        skeletonGroup.rotation.x = baseEuler.x;
        skeletonGroup.rotation.z = baseEuler.z;

        if (postInteractionCompleteRef.current) {
            interactive.setWithinRadius(false);
            const alpha = Math.min(1, delta / ROTATION_DURATION);
            const targetYaw = baseEuler.y + Math.PI;
            skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, targetYaw, alpha);
            return;
        }
       
        if (cineModeTriggeredRef.current) {
            interactive.setWithinRadius(false);
            const alpha = Math.min(1, delta / ROTATION_DURATION);
            skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, baseEuler.y, alpha);
            return;
        }

        const targetGroup = targetRef?.current?.group;
        if (!targetGroup || typeof targetGroup.translation !== "function") {
            interactive.setWithinRadius(false);
            const alpha = Math.min(1, delta / ROTATION_DURATION);
            skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, baseEuler.y, alpha);
            return;
        }

        const translation = targetGroup.translation();
        if (!translation) {
            interactive.setWithinRadius(false);
            const alpha = Math.min(1, delta / ROTATION_DURATION);
            skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, baseEuler.y, alpha);
            return;
        }

        targetWorldPos.set(translation.x, translation.y, translation.z);
        skeletonGroup.getWorldPosition(skeletonWorldPos);

        const distance = skeletonWorldPos.distanceTo(targetWorldPos);
        interactive.setWithinRadius(distance <= interactive.radius, distance);

        let desiredYaw = baseEuler.y;

        if (distance > PROXIMITY_RADIUS) {
            const alpha = Math.min(1, delta / ROTATION_DURATION);
            skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, desiredYaw, alpha);
            return;
        }

        lookDirection.subVectors(targetWorldPos, skeletonWorldPos);
        lookDirection.y = 0;

        if (playerHidden) {
            camera.getWorldPosition(cameraWorldPos);
            lookDirection.subVectors(cameraWorldPos, skeletonWorldPos);
            lookDirection.y = 0;
            if (lookDirection.lengthSq() > 1e-5) {
                lookDirection.normalize();
                desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
            }
        } else if (lookDirection.lengthSq() > 1e-5) {
            lookDirection.normalize();
            desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
        }

        const alpha = Math.min(1, delta / ROTATION_DURATION);
        skeletonGroup.rotation.y = lerpAngle(skeletonGroup.rotation.y, desiredYaw, alpha);
    });

    return (
        <primitive
            ref={group}
            object={clonedScene}
            position={position}
            scale={scale}
            {...props}
        />
    );
}

useGLTF.preload("/interactive/skeleton1.glb");
